/**
 * @pdmg/reporting-blocks — the block registry.
 *
 * Derived from the three 2026 mockups (Display, Streaming Audio, DOOH Pre-Flight).
 * Every entry is the contract between the builder (which configures it), the renderer
 * (which draws it) and the paginator (which places it).
 *
 * Adding a block is a code change in R1 and a code change in R2 as well — the visual
 * builder composes from this registry, it does not author new block types.
 */

import type { BlockManifest } from './template-schema.js';

const ALL = ['PREFLIGHT', 'INTERIM', 'POSTFLIGHT'] as const;
const POST = ['INTERIM', 'POSTFLIGHT'] as const;

export const BLOCK_REGISTRY: BlockManifest[] = [
  // ---------------------------------------------------------------- chrome --
  {
    type: 'PageHeader',
    label: 'Page header band',
    category: 'chrome',
    description: 'Dark gradient band: report title left with accent rule, client name and campaign ID right.',
    props: {
      title: { type: 'string', required: true, description: 'e.g. "Campaign Evaluation Report".' },
      showAccentRule: { type: 'boolean', required: false, default: true, description: 'Cyan rule under the title.' },
      campaignIdLabel: { type: 'string', required: false, default: 'CAMPAIGN ID#:', description: 'Label before the campaign id, e.g. "CAMPAIGN ID#:" or "CAMPAIGN IO#:".' },
    },
    bindings: {
      clientName: { type: 'string', required: true, suggestion: 'campaign.clientName', description: 'Right-hand label.' },
      campaignId: { type: 'string', required: true, suggestion: 'campaign.reportSo', description: 'Shown after the campaign-id label.' },
      logo: { type: 'string', required: false, suggestion: 'presentation.logo', description: 'White-label logo, inlined as a data: URI. Drawn top-left when present and showLogo is on.' },
      showLogo: { type: 'boolean', required: false, suggestion: 'presentation.showLogo', description: 'Whether to draw the logo (default on).' },
    },
    slots: undefined,
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 0.9,
  },
  {
    type: 'PageFooter',
    label: 'Page footer',
    category: 'chrome',
    description:
      'Copyright line and "Page N of M". Page numbers are computed by the render service, never authored — ' +
      'the mockups get this wrong in two of three files (scope §4.3).',
    props: {},
    bindings: {
      companyName: { type: 'string', required: true, suggestion: 'campaign.agencyName', description: 'Copyright holder.' },
      year: { type: 'string', required: true, suggestion: '$substring(report.periodEnd, 0, 4)', description: 'Derived, never hardcoded.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 0.4,
  },

  // -------------------------------------------------------------- identity --
  {
    type: 'CampaignMetaCard',
    label: 'Campaign details',
    category: 'identity',
    description: 'Icon-labelled rows: client, start, end, campaign ID. DOOH uses the left-aligned variant.',
    props: {
      variant: { type: 'enum', required: false, default: 'icons', values: ['icons', 'labels'], description: 'Icon rows or a plain label/value list.' },
      showNote: { type: 'boolean', required: false, default: false, description: 'Render the campaign note paragraph.' },
      note: { type: 'string', required: false, description: 'A standing caveat authored on the block — the pre-flight disclaimer, not a per-campaign figure.' },
      campaignIdLabel: { type: 'string', required: false, default: 'Campaign ID', description: 'Label for the id row, e.g. "Campaign ID" or "Campaign IO".' },
      nameIcon: { type: 'string', required: false, description: 'Icon for the campaign-name row (defaults to the campaign-ID mark).' },
    },
    bindings: {
      clientName: { type: 'string', required: true, suggestion: 'campaign.clientName', description: '' },
      /* Pre-flight names the campaign as well as its client; display and audio do not. */
      campaignName: { type: 'string', required: false, suggestion: 'campaign.name', description: 'Shown when present, as the pre-flight sheet does.' },
      startDate: { type: 'string', required: true, suggestion: 'campaign.startDate', description: '' },
      endDate: { type: 'string', required: true, suggestion: 'campaign.endDate', description: '' },
      campaignId: { type: 'string', required: true, suggestion: 'campaign.reportSo', description: '' },
      note: { type: 'string', required: false, suggestion: 'campaign.notes', description: '' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 2.2,
  },
  {
    type: 'CreativePreview',
    label: 'Creative preview',
    category: 'identity',
    description: 'Single creative image with optional "representative of creative used" footnote.',
    props: {
      hideWhenEmpty: { type: 'boolean', required: false, default: false, description: 'Drop the box entirely when there is no artwork, rather than an empty frame.' },
      footnote: { type: 'string', required: false, description: 'Small print under the image.' },
      caption: { type: 'string', required: false, description: 'Printed under the artwork — the mockups note the creative is representative.' },
      maxHeightIn: { type: 'number', required: false, default: 2.4, description: '' },
      bare: { type: 'boolean', required: false, default: false, description: 'Float the artwork with no card frame/background/shadow.' },
    },
    bindings: { assetId: { type: 'string', required: true, suggestion: 'campaign.bannerAssetId', description: '' } },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 2.4,
  },

  // ---------------------------------------------------------------- metric --
  {
    type: 'KpiTileRow',
    label: 'KPI tiles',
    category: 'metric',
    description: '2–4 tiles, each an icon, a label and a large value. Labels differ per channel.',
    props: {
      columns: { type: 'number', required: false, default: 4, description: 'Tiles per row (2–4).' },
    },
    bindings: {
      /** array of { label, value, icon } — assembled by the template, not hardcoded */
      tiles: { type: 'array', required: true, suggestion: '[]', description: 'Array of {label, value, icon, format?}. format: "pct" renders a rate; omitted renders an integer.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'tbd',
    nominalHeightIn: 1.5,
  },
  {
    type: 'DeliveryStrip',
    label: 'Delivery / pacing strip',
    category: 'metric',
    description: 'Bordered strip. Either the display contract cells (contracted / over-delivered / '
      + 'over-delivery % / CTR*) or a generic `metrics` array ([{label, value, format}]) — the Eblast '
      + 'summary uses the latter for Targeted Records / Total Website Clicks. Carries the CTR footnote.',
    props: {
      ctrFootnote: {
        type: 'string', required: false,
        default: 'CTR is calculated against contracted impressions.',
        description: 'The mockups show CTR* with no footnote anywhere — supply it here.',
      },
    },
    // One of `metrics` OR the four contract fields is supplied; none is required on its own, so all are
    // optional and the block picks the mode from what is bound.
    bindings: {
      metrics: { type: 'array', required: false, suggestion: '[]', description: 'Generic cells: array of {label, value, format?}. Overrides the contract cells when present.' },
      contracted: { type: 'number', required: false, suggestion: 'overall.contract.targetImpressions', description: '' },
      overDelivered: { type: 'number', required: false, suggestion: 'overall.derived.overDeliveredImpressions', description: '' },
      overDeliveryPct: { type: 'number', required: false, suggestion: 'overall.derived.overDeliveryPct', description: '' },
      ctr: { type: 'number', required: false, suggestion: 'overall.derived.ctr', description: '' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'tbd',
    nominalHeightIn: 0.9,
  },
  {
    type: 'MetricTrio',
    label: 'Three-metric panel',
    category: 'metric',
    description: 'Audio summary: Ads Played, Reach %, LTR %.',
    props: {},
    bindings: {
      metrics: { type: 'array', required: true, suggestion: '[]', description: 'Array of {label, value, icon}.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'tbd',
    nominalHeightIn: 1.8,
  },
  {
    type: 'ContractedVsActual',
    label: 'Contracted vs actual',
    category: 'metric',
    description: 'Side-by-side pair. Renders TBD for actuals pre-flight — the DOOH mockup case.',
    props: {
      contractedIcon: { type: 'string', required: false, default: 'eye', description: 'Icon name for the contracted figure (DOOH passes its billboard mark).' },
      actualIcon: { type: 'string', required: false, default: 'eye', description: 'Icon name for the actual figure.' },
    },
    bindings: {
      contracted: { type: 'number', required: true, suggestion: 'overall.contract.targetImpressions', description: '' },
      actual: { type: 'number', required: false, suggestion: 'dooh.actualImpressions', description: 'Null pre-flight.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'tbd',
    nominalHeightIn: 0.9,
  },

  // ------------------------------------------------------------- breakdown --
  {
    type: 'DeviceCategoryCard',
    label: 'Device category',
    category: 'breakdown',
    description: 'Mobile vs desktop with the device illustration. Sums to total clicks.',
    props: {      artHeightPx: { type: 'number', required: false, default: 46, description: 'Device illustration height; smaller on dense pages.' },
    },
    bindings: {
      mobile: { type: 'number', required: true, suggestion: 'overall.delivery.mobileClicks', description: '' },
      desktop: { type: 'number', required: true, suggestion: 'overall.delivery.desktopClicks', description: '' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'tbd',
    nominalHeightIn: 1.6,
  },
  {
    type: 'DeviceDetailsTable',
    label: 'Device details',
    category: 'breakdown',
    description: 'Name/count rows. NOTE: sums to MOBILE clicks, not total — see scope §4.1.',
    props: {
      maxRows: { type: 'number', required: false, default: 8, description: 'Remainder folds into "Other".' },
      title: { type: 'string', required: false, default: 'Device Details', description: 'Card title (eblast uses "Mobile Device Details").' },
      showNote: { type: 'boolean', required: false, default: true, description: 'Show the "sum to mobile clicks" footnote.' },
    },
    bindings: { rows: { type: 'array', required: true, suggestion: 'overall.devices', description: 'Array of {name, clicks}.' } },
    flow: 'breakable',
    repeatHeaderOnBreak: true,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 1.8,
  },
  {
    type: 'BrowserTypeCard',
    label: 'Browser type',
    category: 'breakdown',
    description: 'Browser logos with counts. Sums to TOTAL clicks. Assets ship in rtb_ui/src/assets/browser-logos as SVG.',
    props: {
      maxItems: { type: 'number', required: false, default: 5, description: '' },
      imageLogos: { type: 'boolean', required: false, default: false, description: 'Use the approved PNG browser marks (eblast) instead of drawn SVG logos.' },
    },
    bindings: { rows: { type: 'array', required: true, suggestion: 'overall.browsers', description: 'Array of {name, clicks}.' } },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 1.6,
  },
  {
    type: 'CampaignChannelsCard',
    label: 'Campaign channels',
    category: 'breakdown',
    description: 'Channel icons with labels — Display Ads, Retargeting, Streaming Audio.',
    props: { iconSize: { type: 'number', required: false, default: 68, description: 'V2 channel icon size (px).' } },
    bindings: {
      channels: { type: 'array', required: true, suggestion: 'campaign.channels', description: '' },
      /*
       * Retargeting is presented as a channel and is not one in the data model.
       *
       * The mockup lists it beside Display Ads because that is how the buy is described
       * to a client. Deriving it from retargeted clicks keeps a presentation decision
       * out of the report data model, where a `RETARGETING` enum value would have to be
       * written by generation and would then mean something different from the other
       * members.
       */
      retargetClicks: { type: 'number', required: false, suggestion: 'overall.delivery.retargetClicks', description: 'Non-zero (or non-zero retargetImpressions) adds Retargeting to the card.' },
      retargetImpressions: { type: 'number', required: false, suggestion: 'overall.delivery.retargetImpressions', description: 'Non-zero adds Retargeting even when the retargeting drew no clicks.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 1.6,
  },

  // -------------------------------------------------------------- targeting --
  {
    type: 'GeoTargetingMap',
    label: 'Geographic targeting',
    category: 'targeting',
    description: 'Offline SVG map (open Census boundaries) with red targeting overlays and a caption row; or a static map image when one is supplied.',
    props: {
      heightIn: { type: 'number', required: false, default: 5.2, description: '' },
      cityFootnote: { type: 'string', required: false, description: 'Overrides the city-mode caption footnote (e.g. email maps click geography, not targeting).' },
    },
    bindings: {
      // Optional: a generated report has no rendered static-map asset (we do not call a map
      // service at generation time), but the ZIP + radius caption below is meaningful on its own.
      // Required-and-`hide` would drop the whole block — caption included — on every real report.
      mapAssetId: { type: 'string', required: false, suggestion: 'targeting.mapAssetId', description: '' },
      zips: { type: 'array', required: false, suggestion: 'targeting.zips', description: '' },
      radiusMiles: { type: 'number', required: false, suggestion: 'targeting.radiusMiles', description: '' },
      // A Mapbox static-map image (data URI) baked at generation — preferred over the SVG when present.
      mapImage: { type: 'string', required: false, suggestion: 'targeting.mapImage', description: 'Mapbox static map (data URI).' },
      // Bind `geoMode` to switch this block to the offline open-source SVG map; the entity lists feed
      // the chosen mode. Unbound (the white-label default) keeps the legacy static-image behaviour.
      geoMode: { type: 'string', required: false, suggestion: 'targeting.geoMode', description: 'country | state | zip | city | county | dma.' },
      states: { type: 'array', required: false, suggestion: 'targeting.states', description: 'USPS codes / FIPS / names.' },
      counties: { type: 'array', required: false, suggestion: 'targeting.counties', description: '5-digit county FIPS.' },
      cities: { type: 'array', required: false, suggestion: 'targeting.cities', description: '"City, ST".' },
      dmas: { type: 'array', required: false, suggestion: 'targeting.dmas', description: 'DMA code or name.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 5.6,
  },
  {
    type: 'AudienceTargetingCard',
    label: 'Audience targeting',
    category: 'targeting',
    description: 'Heading plus bullet list of demographic criteria.',
    props: {},  // the heading was removed — the section leads with the campaign/flight name (review)
    // Optional: an empty list is a valid state (a broadly-targeted campaign). The block draws its
    // own "broad reach" note then, so it must NOT be hidden by `unavailableRendering` when empty.
    bindings: { items: { type: 'array', required: false, suggestion: 'targeting.audience.notes', description: '' } },
    flow: 'breakable',
    repeatHeaderOnBreak: true,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 1.4,
  },
  {
    type: 'AttributeCards',
    label: 'Attribute cards',
    category: 'targeting',
    description: 'Small cards in a row — Language, Age Group, Gender.',
    props: { columns: { type: 'number', required: false, default: 3, description: '' } },
    bindings: { cards: { type: 'array', required: true, suggestion: '[]', description: 'Array of {label, value}.' } },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 1.2,
  },
  {
    type: 'TopSitesList',
    label: 'Top sites',
    category: 'targeting',
    description: 'Two-column list of domains.',
    props: { columns: { type: 'number', required: false, default: 2, description: '' } },
    // Optional: empty until delivery is consolidated by publisher. The block draws its own note
    // then, so an empty list must not hide it via `unavailableRendering`.
    bindings: { sites: { type: 'array', required: false, suggestion: 'targeting.topSites', description: '' } },
    flow: 'breakable',
    repeatHeaderOnBreak: true,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 1.4,
  },

  // ----------------------------------------------------------------- detail --
  {
    type: 'CreativeDetailList',
    label: 'Creative details',
    category: 'detail',
    description: 'Repeating rows: thumbnail, dimensions, destination URL, clicks. Grouped by ad group.',
    props: {
      showClicks: { type: 'boolean', required: false, default: true, description: 'Off for pre-flight.' },
      maxHeightIn: { type: 'number', required: false, default: 1.05, description: 'Creative artwork height cap (inches).' },
      factsVariant: { type: 'string', required: false, default: '', description: '"icons" for the ring-badge fact rows.' },
    },
    bindings: { creatives: { type: 'array', required: true, suggestion: 'adGroups[0].creatives', description: '' } },
    flow: 'breakable',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'tbd',
    nominalHeightIn: 8.0,
  },
  {
    type: 'PaginatedTable',
    label: 'Paginated table',
    category: 'detail',
    description:
      'Flows across pages with a repeating header row. Drives the DOOH venue list, which spans pages 2–4+ ' +
      'of that mockup. The hardest block to get right; prototype early.',
    props: {
      columns: { type: 'string', required: true, description: 'JSON array of {key, label, widthPct, breakWords?}.' },
      zebra: { type: 'boolean', required: false, default: true, description: '' },
      breakWords: { type: 'boolean', required: false, default: false, description: 'Force word-break on all cells (long URLs wrap instead of overflowing).' },
      fixedLayout: { type: 'boolean', required: false, default: false, description: 'table-layout:fixed so column widthPct is honored exactly.' },
      maxRows: {
        type: 'number', required: false, default: 100,
        description: 'Hard cap on rendered rows. DOOH placements cap at 100 (D42); the remainder is '
          + 'disclosed via showingOf and summarised by a companion block.',
      },
      showingOf: {
        type: 'boolean', required: false, default: true,
        description: 'Render "showing N of M" when the row set was capped. Never silently truncate — '
          + 'a bounded list presented as complete is a misrepresentation.',
      },
      ellipsizeCells: {
        type: 'boolean', required: false, default: false,
        description: 'Clip each cell to one line with a trailing ellipsis instead of wrapping. Makes '
          + 'row height constant regardless of content length — required inside an atomic container '
          + '(a Columns layout), where a long value would otherwise wrap and overflow the fixed row.',
      },
    },
    bindings: {
      rows: { type: 'array', required: true, suggestion: 'dooh.potentialVenues', description: '' },
      totalRows: {
        type: 'number', required: false, suggestion: 'dooh.totalPotentialVenues',
        description: 'Total before capping, for the "of M" figure.',
      },
    },
    flow: 'breakable',
    repeatHeaderOnBreak: true,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 9.0,
  },
  {
    type: 'VenueTypeSummary',
    label: 'Venue type summary',
    category: 'detail',
    description:
      'Counts by venue type across ALL matching placements, including those beyond the table cap. '
      + 'The completeness claim the capped list can no longer make on its own (D42).',
    props: {
      columns: { type: 'number', required: false, default: 2, description: '' },
      noun: { type: 'string', required: false, default: 'matching placements', description: 'What the total counts, for the "Across all N …" line.' },
    },
    bindings: {
      rows: { type: 'array', required: true, suggestion: 'dooh.venueSummary', description: 'Array of {venueType, screens}.' },
      total: { type: 'number', required: false, suggestion: 'dooh.totalPotentialVenues', description: '' },
    },
    flow: 'breakable',
    repeatHeaderOnBreak: true,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 2.2,
  },
  {
    type: 'PinMap',
    label: 'Pin map',
    category: 'targeting',
    description: 'Static map with pins — DOOH potential screen placements.',
    props: { heightIn: { type: 'number', required: false, default: 5.0, description: '' } },
    bindings: { mapAssetId: { type: 'string', required: true, suggestion: 'dooh.mapAssetId', description: '' } },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 5.4,
  },

  // ----------------------------------------------------------------- chart --
  /*
   * Two chart manifests, not three, and not one.
   *
   * The legacy email report draws three Google Charts: a LineChart (clicks by day), a
   * ColumnChart (open rate vs CTR per drop) and a PieChart (clicks by city). But a
   * line and a column chart consume exactly the same data — categories crossed with
   * named series — so they are one manifest with a `chartType` prop. A pie chart
   * consumes label/value pairs and cannot express series at all, so it is its own.
   *
   * The rule: one manifest per DATA CONTRACT, not per visual. Splitting by appearance
   * would give R2's builder three near-identical binding panels; merging everything
   * would give it one panel whose required fields depend on a dropdown.
   *
   * Both render as SSR SVG, which removes the legacy chart-to-image pass entirely
   * (F5, scope §5.5).
   */
  {
    type: 'SeriesChart',
    label: 'Bar / line chart',
    category: 'chart',
    description:
      'Categories crossed with one or more named series, drawn as grouped bars or lines. '
      + 'Covers the email opens-vs-clicks and clicks-by-day charts.',
    props: {
      chartType: { type: 'enum', required: false, default: 'bar', values: ['bar', 'line'], description: 'Visual form. The data contract is identical either way.' },
      heightIn: { type: 'number', required: false, default: 2.6, description: '' },
      xLabel: { type: 'string', required: false, description: 'Axis caption.' },
      yLabel: { type: 'string', required: false, description: 'Axis caption.' },
      valueFormat: { type: 'enum', required: false, default: 'int', values: ['int', 'pct'], description: 'How values are labelled on the axis and in the legend.' },
      showLegend: { type: 'boolean', required: false, default: true, description: 'Suppress for a single unnamed series.' },
      maxCategories: { type: 'number', required: false, default: 40, description: 'Beyond this the axis is unreadable; excess is dropped and disclosed.' },
    },
    bindings: {
      categories: { type: 'array', required: true, suggestion: 'email.drops.label', description: 'X-axis labels, in order.' },
      series: { type: 'array', required: true, suggestion: '[]', description: 'Array of {name, values[]}; each values[] aligns with categories.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 3.0,
  },
  {
    type: 'GeoHeatMap',
    label: 'Geographic heatmap',
    category: 'chart',
    description:
      'A US state choropleth shading each state by a count (impressions) against the busiest state. '
      + 'Drives the CTV "impressions by geography" map. SSR SVG, theme-coloured.',
    props: {
      heightIn: { type: 'number', required: false, default: 3.2, description: 'Map height in inches.' },
    },
    bindings: {
      regions: { type: 'array', required: true, suggestion: 'ctv.geoStateMap', description: 'Array of {state (USPS/name), impressions}.' },
      geoMode: { type: 'string', required: false, suggestion: 'targeting.geoMode', description: 'Targeting overlay mode: state | zip (else none).' },
      states: { type: 'array', required: false, suggestion: 'targeting.states', description: 'Targeted state codes, for the overlay.' },
      zips: { type: 'array', required: false, suggestion: 'targeting.zips', description: 'Targeted ZIPs, for the radius-circle overlay.' },
      radiusMiles: { type: 'number', required: false, suggestion: 'targeting.radiusMiles', description: 'ZIP radius in miles.' },
      counties: { type: 'array', required: false, suggestion: 'targeting.counties', description: 'Targeted county FIPS, for the county outline.' },
      countyLabels: { type: 'array', required: false, suggestion: 'targeting.countyLabels', description: 'County display names, for the caption row.' },
      mapImage: { type: 'string', required: false, suggestion: 'targeting.mapImage', description: 'Mapbox Streets static map (data: URI) baked at generation; drawn instead of the offline SVG for local targeting.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 3.4,
  },
  {
    type: 'GeoCityBreakdown',
    label: 'Geographic city breakdown',
    category: 'detail',
    description:
      'Delivery cities scoped to the campaign targeting (inside the ZIP radius or target states), as a '
      + 'top-N table with a reconciling "Other" row. Drives the CTV scoped "top cities" breakdown.',
    props: {
      maxRows: { type: 'number', required: false, default: 10, description: 'How many cities before the Other row.' },
    },
    bindings: {
      cities: { type: 'array', required: true, suggestion: 'ctv.geoCities', description: 'Array of {city, state, impressions} — the unscoped delivery cities.' },
      geoMode: { type: 'string', required: false, suggestion: 'targeting.geoMode', description: 'Targeting mode: zip | state | country.' },
      states: { type: 'array', required: false, suggestion: 'targeting.states', description: 'Targeted state codes.' },
      zips: { type: 'array', required: false, suggestion: 'targeting.zips', description: 'Targeted ZIPs (scope by radius).' },
      radiusMiles: { type: 'number', required: false, suggestion: 'targeting.radiusMiles', description: 'ZIP radius in miles.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 2.4,
  },
  {
    type: 'DayHourHeatmap',
    label: 'Day / hour heatmap',
    category: 'chart',
    description:
      'A 7×24 matrix of a count (impressions) by day of week (Sun–Sat) and hour (0–23), shaded by '
      + 'intensity. Drives the CTV "views by day and time" panel. SSR SVG.',
    props: {
      caption: { type: 'string', required: false, description: 'Footnote under the grid (timezone / legend).' },
    },
    bindings: {
      matrix: { type: 'array', required: true, suggestion: 'ctv.viewsByDayHour', description: 'A 7×24 array of numbers: row 0 = Sunday … 6 = Saturday, column 0-23 = hour.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 2.4,
  },
  {
    type: 'PieChart',
    label: 'Pie chart',
    category: 'chart',
    description: 'Label/value slices with a legend. Covers the email clicks-by-city chart.',
    props: {
      heightIn: { type: 'number', required: false, default: 2.6, description: '' },
      maxSlices: {
        type: 'number', required: false, default: 8,
        description: 'Slices beyond this are folded into "Other" rather than dropped — a pie whose slices do not sum to the whole misrepresents the total.',
      },
      showLegend: { type: 'boolean', required: false, default: true, description: '' },
    },
    bindings: {
      slices: { type: 'array', required: true, suggestion: 'email.citiesByClicks', description: 'Array of {label, value}.' },
    },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...POST],
    unavailableRendering: 'hide',
    nominalHeightIn: 3.0,
  },

  // ---------------------------------------------------------------- content --
  {
    type: 'RichTextBlock',
    label: 'Rich text',
    category: 'content',
    description: 'Sanitised rich text — music genres, notes, disclaimers. Supports inline bindings.',
    props: {
      title: { type: 'string', required: false, description: 'Card header, omitted if absent.' },
      size: { type: 'enum', required: false, values: ['body', 'lead', 'small', 'note'], default: 'body', description: 'A lead line reads as a statement; small is for long lists set as a paragraph, as the mockup sets music genres; note is footnote fine print.' },
    },
    bindings: { html: { type: 'string', required: true, suggestion: '""', description: 'Sanitised before render.' } },
    flow: 'breakable',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 1.6,
  },
  {
    type: 'CustomHtml',
    label: 'Custom HTML',
    category: 'content',
    description:
      'Escape hatch (scope D3). HTML is sanitised, CSS auto-scoped to the instance. Always atomic: ' +
      'author-supplied markup cannot be safely split across a page boundary.',
    props: { html: { type: 'string', required: true, description: 'Sanitised with a print-safe allowlist.' } },
    bindings: {},
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 2.0,
  },
  {
    type: 'Columns',
    label: 'Column layout',
    category: 'content',
    description:
      'Lays its children side by side in equal columns. The catalog\'s only layout primitive — '
      + 'without it every block stacks in one column and a designed summary page cannot be '
      + 'expressed at all.',
    props: {
      columns: { type: 'number', required: false, default: 2, description: 'Number of equal columns (2-4).' },
      align: { type: 'enum', required: false, default: 'stretch', values: ['stretch', 'start'], description: 'Cross-axis alignment.' },
      weights: {
        type: 'string', required: false,
        description: 'Comma-separated flex weights per column, e.g. "2,3" for a narrow-left / wide-right '
          + 'split. Omit for equal columns.',
      },
    },
    bindings: {},
    slots: ['children'],
    // Atomic: splitting a side-by-side group across a page boundary would separate
    // content that is adjacent precisely because it belongs together.
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
  },
  {
    type: 'SectionCard',
    label: 'Section card',
    category: 'content',
    description: 'Container: navy title bar with icon, bordered body. Holds other blocks.',
    props: {
      title: { type: 'string', required: true, description: '' },
      /*
       * A glyph NAME from the report icon set, not an asset id.
       *
       * Declared as an asset originally, which nothing ever supplied — every card
       * heading in the mockups carries a drawn glyph, and those ship as inline SVG so
       * that nothing is fetched at print time (R4).
       */
      icon: { type: 'string', required: false, description: 'Heading glyph from the report icon set.' },
      titlecase: { type: 'boolean', required: false, default: false, description: 'Set a real title in mixed case rather than an uppercase label.' },
      rightText: { type: 'string', required: false, description: 'Static right-aligned label in the heading.' },
    },
    bindings: {
      rightText: { type: 'string', required: false, description: 'Right-aligned heading label, bound — e.g. the ad group name on campaign details.' },
      title: { type: 'string', required: false, description: 'Heading text, bound — overrides the static title (e.g. "Impressions by Week" on a long flight).' },
    },
    slots: ['body'],
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
  },
  {
    type: 'ProvenanceNote',
    label: 'Adjusted-figures note',
    category: 'content',
    description:
      'Renders a disclosure when a report contains human overrides (scope R2/R6). Not in the mockups — ' +
      'added because approval-gated distribution makes silent adjustment indefensible.',
    props: {
      template: {
        type: 'string', required: false,
        default: 'Certain figures in this report have been adjusted. See the accompanying audit record.',
        description: '',
      },
    },
    bindings: { show: { type: 'boolean', required: true, suggestion: 'provenance.hasAdjustedFigures', description: '' } },
    flow: 'atomic',
    repeatHeaderOnBreak: false,
    lifecycle: [...ALL],
    unavailableRendering: 'hide',
    nominalHeightIn: 0.5,
  },
];
